import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SharedService } from '../services/shared.service';
import { PatientService } from '../services/patient.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.css']
})
export class PatientComponent implements OnInit, AfterViewInit {
  @ViewChild('patientNameInput', { static: false }) patientNameInput: ElementRef;
  showLoadingMessage = false;

  /** Full list from API (active + inactive), each item has isActive */
  allPatientDtos: any[] = [];
  residentName = '';
  /** Client-side filter: substring match on patient id (numeric id as string) */
  patientIdFilter = '';
  /** When true: show only inactive; when false: show only active */
  showOnlyInactive = false;

  constructor(
    private sharedService: SharedService,
    private patientService: PatientService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.sharedService.reloadUserPrivileges();
    this.loadAllPatientDtos();
  }

  ngAfterViewInit(): void {
    if (this.patientNameInput && this.patientNameInput.nativeElement) {
      this.patientNameInput.nativeElement.focus();
    }
  }

  /** Load active and inactive lists once, merge with isActive flag */
  loadAllPatientDtos(): void {
    this.showLoadingMessage = true;
    const active$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      true
    );
    const inactive$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      false
    );
    forkJoin([active$, inactive$]).subscribe({
      next: ([activeList, inactiveList]) => {
        activeList.forEach((p: any) => (p.isActive = true));
        inactiveList.forEach((p: any) => (p.isActive = false));
        this.allPatientDtos = [...(activeList || []), ...(inactiveList || [])];
        this.showLoadingMessage = false;
      },
      error: () => (this.showLoadingMessage = false)
    });
  }

  /** Ignore commas so "Last, First" matches the same as "Last First". */
  private normalizePatientNameSearchTerm(value: string): string {
    return (value || '')
      .toLowerCase()
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Client-side filtered list: inactive/active, Resident Name, Patient ID (substring on id) */
  get filteredPatientDtos(): any[] {
    if (!this.allPatientDtos || this.allPatientDtos.length === 0) return [];
    let list = this.showOnlyInactive
      ? this.allPatientDtos.filter((p) => !p.isActive)
      : this.allPatientDtos.filter((p) => p.isActive);
    const term = this.normalizePatientNameSearchTerm(this.residentName);
    if (term) {
      list = list.filter((p) => {
        const fullName = this.normalizePatientNameSearchTerm(
          (p.lastName || '') + ' ' + (p.firstName || '')
        );
        const fullNameReversed = this.normalizePatientNameSearchTerm(
          (p.firstName || '') + ' ' + (p.lastName || '')
        );
        return fullName.indexOf(term) !== -1 || fullNameReversed.indexOf(term) !== -1;
      });
    }
    const idTerm = (this.patientIdFilter || '').trim();
    if (idTerm) {
      list = list.filter((p) => {
        const idStr = p.id != null ? String(p.id) : '';
        return idStr.indexOf(idTerm) !== -1;
      });
    }
    return list;
  }

  openProfile(patientId: number): void {
    this.router.navigate(['patient/profile/' + patientId]);
  }
}
